import { Row, TableColumnsType, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { HTMLAttributes, useState } from 'react';
import { ButtonType, TAButton } from '../components';
import DataDisplay from '../components/base/data-display/data-display-selector';
import { ApplicantsStateCount } from '../components/dashboard/styles';
import { ApplicantStates } from '../constants/applicant-states';
import { JobStatusMap } from '../constants/job-status';
import { IOutsourcingJobPost, Identifiable } from '../types';
import { OutsourcingCandidatesList } from './outsourcing-candidates-list';

interface OutsourcingJopPostsListProps {
	data: any;
	loading: boolean;
	pagination: any;
	outSourcingBenefitsSelected: boolean;
	onOutsourceSubmit: (outsourceRequest: any) => void;
}

const OutsourcingJobPostItemFactory =
	(outSourcingBenefitsSelected, setSelectedJobToOutsource) =>
	({ item }) => {
		return (
			<div className="d-flex flex-column w-100 ps-3">
				<Row className="d-flex justify-content-center mb-1">{item.title}</Row>

				<Row className="d-flex justify-content-center mb-2">
					posted: {dayjs(item.createdAt).format('ddd, MMM D, YYYY h:mm A')}
				</Row>

				<Row className="mb-3">
					{Object.keys(item).map(state => {
						return ApplicantStates[state] ? (
							<ApplicantsStateCount count={item[state]} name={ApplicantStates[state]} size="mobile" />
						) : null;
					})}
				</Row>
				<Row className="d-flex justify-content-center mb-2">
					<TAButton
						btntype={ButtonType.PRIMARY}
						disabled={!(item.hired || item.offer) || !outSourcingBenefitsSelected}
						onClick={() => {
							setSelectedJobToOutsource(item.id);
						}}
					>
						Outsource
					</TAButton>
				</Row>
			</div>
		);
	};

export const OutsourcingJopPostsList: React.FC<HTMLAttributes<HTMLDivElement> & OutsourcingJopPostsListProps> = ({
	data,
	loading,
	pagination,
	outSourcingBenefitsSelected,
	onOutsourceSubmit
}) => {
	const [selectedJobToOutsource, setSelectedJobToOutsource] = useState<string>();

	const tableColumns: TableColumnsType<Identifiable<IOutsourcingJobPost>> = [
		{
			title: (
				<span className="d-inline-block p-1 w-100" style={{ color: '#8DABC4' }}>
					Title
				</span>
			),
			dataIndex: 'title',
			key: 'title',
			align: 'center'
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Status
				</span>
			),
			dataIndex: 'status',
			key: 'status',
			render: status => <Tag color={JobStatusMap[status]}>{status}</Tag>
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Interested
				</span>
			),
			dataIndex: 'interested',
			key: 'interested'
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					To Interview
				</span>
			),
			dataIndex: 'toInterview',
			key: 'toInterview'
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Offer
				</span>
			),
			dataIndex: 'offer',
			key: 'offer'
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Hired
				</span>
			),
			dataIndex: 'hired',
			key: 'hired'
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Rejected
				</span>
			),
			dataIndex: 'rejected',
			key: 'rejected'
		},

		{
			title: (
				<span className="d-inline-block p-1 w-100" style={{ color: '#8DABC4' }}>
					PostedAt
				</span>
			),
			dataIndex: 'createdAt',
			key: 'createdAt',
			align: 'center',
			render: text => (
				<span className="px-1 d-flex justify-content-center">
					{dayjs(text).format('ddd, MMM D, YYYY h:mm A')}
				</span>
			)
		},
		{
			title: (
				<span className="d-inline-block py-2" style={{ color: '#8DABC4' }}>
					Actions
				</span>
			),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Tooltip
					title={
						!(record.hired || record.offer || record.toInterview) || record.status !== 'Open'
							? 'Please make sure you have some candidats in interview, offer or hired states and the job is open'
							: null
					}
				>
					<TAButton
						btntype={ButtonType.PRIMARY}
						disabled={!(record.hired || record.offer || record.toInterview) || record.status !== 'Open'}
						onClick={() => {
							setSelectedJobToOutsource(record.id);
						}}
					>
						Outsource
					</TAButton>
				</Tooltip>
			)
		}
	];

	return (
		<>
			<DataDisplay
				className="ta-table"
				data={data ?? []}
				mobileData={data?.length ? [data] : []}
				loading={loading}
				tableColumns={tableColumns}
				listItemComponent={OutsourcingJobPostItemFactory(
					outSourcingBenefitsSelected,
					setSelectedJobToOutsource
				)}
				size="small"
				pagination={pagination}
			/>

			{selectedJobToOutsource ? (
				<OutsourcingCandidatesList
					jobId={selectedJobToOutsource}
					onCancel={() => {
						setSelectedJobToOutsource(undefined);
					}}
					onOutsourceSubmit={onOutsourceSubmit}
				/>
			) : (
				false
			)}
		</>
	);
};
